import { useEffect } from "react"
import { Link, useSearchParams } from "react-router"
import { useQueryClient } from "@tanstack/react-query"
import cx from "classnames"

import WizardNavigation from "../components/WizardNavigation"
import LiabilityInsuranceContent from "../sections/LiabilityInsuranceWizard/LiabilityInsuranceContent"
import { useLiabilityInsuranceStore } from "../stores/useLiabilityInsuranceStore"
import {
  useAccount,
  useAccountId,
  useGetInsuranceWizard,
  useProfile,
} from "../hooks"
import { useIsCheckoutSuccess } from "../hooks/useIsCheckoutSuccess"
import { useAccessToken } from "../stores"
import { LiabilityInsuranceTypes } from "@/types/liabilityInsuranceWizard"
import { AccountTypes } from "@/types/account"
import { Profile } from "@/types"

const LiabilityInsuranceWizard = () => {
  const queryClient = useQueryClient()
  const accountId = useAccountId()
  const accessToken = useAccessToken()
  const [searchParams, setSearchParams] = useSearchParams()

  const step = searchParams.get("step")

  const { enabledSteps, setEnabledSteps, updateEnabledSteps } =
    useLiabilityInsuranceStore()

  const handleActiveStep = (newActiveStep: number) => {
    searchParams.set("step", String(newActiveStep))

    setSearchParams(searchParams)
  }

  const { data: insuranceWizardData, isLoading: isInsuranceDataLoading } =
    useGetInsuranceWizard<LiabilityInsuranceTypes, Error>(
      queryClient,
      accountId
    )

  const { data: account, isLoading: accountIsLoading } = useAccount<
    AccountTypes,
    Error
  >(queryClient, accountId)

  const { data: profile, isLoading: profileIsLoading } = useProfile<
    Profile,
    Error
  >(queryClient, {
    enabled: !!accessToken,
  })

  const isLoadingArr = [
    isInsuranceDataLoading,
    accountIsLoading,
    profileIsLoading,
  ]

  const isLoading = isLoadingArr.some(Boolean)

  useIsCheckoutSuccess()

  useEffect(() => {
    if (insuranceWizardData?.completed) {
      setEnabledSteps([false, false, false, false, true])
      return
    }

    const currentStep = Number(step)
    if (Number.isNaN(currentStep) || currentStep < 1 || currentStep > 5) return

    updateEnabledSteps(currentStep)
  }, [
    insuranceWizardData?.completed,
    setEnabledSteps,
    updateEnabledSteps,
    step,
  ])

  return (
    <div className="bg-white min-h-full">
      <div className="relative w-full flex lg:justify-center pt-5 lg:pt-10 px-5 lg:px-10">
        <WizardNavigation
          navItems={[
            {
              className: "liability-insurance-nav-item-page-1",
              text: "1. Coverage",
            },
            {
              className: "liability-insurance-nav-item-page-2",
              text: "2. Property",
            },
            {
              className: "liability-insurance-nav-item-page-3",
              text: "3. Quote",
            },
            {
              className: "liability-insurance-nav-item-page-4",
              text: "4. Finalize",
            },
            {
              className: "liability-insurance-nav-item-page-5",
              text: "5. Confirmation",
            },
          ]}
          activeStep={Number(step)}
          enabledSteps={enabledSteps}
          handleActiveStep={handleActiveStep}
          testid="insurance-wizard-navigation"
        />

        <Link
          to="/"
          className={cx(
            "liability-insurance-close-button",
            "absolute right-5 xl:right-10 top-5 lg:top-10 flex items-center justify-center",
            "text-3xl w-6 h-6 text-dusk-500 leading-6 tracking-[0.02em] cursor-pointer"
          )}
          aria-label="Close Government Wizard"
        >
          &times;
        </Link>
      </div>

      <LiabilityInsuranceContent
        isLoading={isLoading}
        insuranceWizardData={insuranceWizardData}
        account={account}
        profile={profile}
      />
    </div>
  )
}

export default LiabilityInsuranceWizard
